import React, {useEffect, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import Grid from '@material-ui/core/Grid';
import {useStore} from 'effector-react';
import i18next from 'i18next';
import {Control, Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import {filterFactories, filterFactoryLines} from 'api/orders';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {AsyncSelect, Select, SelectOption} from 'components/ui-kit/Select';
import {firstStep$, firstStepChanged, stepChanged} from 'models/orders/create';
import {profileCountriesState$} from 'models/profile/common';
import {OrderFormData} from 'types';

import {FactoriesListRawRow, ProfileCountries} from 'types/profile';

import {FormContainer, NavigationButtonsContainer} from '../styled';

const schema = yup.object().shape({
    country: yup.string().required(i18next.t('components.form.required')),
    factoryId: yup.string().required(i18next.t('components.form.required')),
    productionLineId: yup.string().required(i18next.t('components.form.required')),
});

interface FormItemProps {
    control: Control;
    defaultValue?: string;
    name: string;
    setValue?: Function;
    fieldName?: string;
    getValues: Function;
    trigger?: Function;
    watch: Function;
}

export const FactoryId = ({
    control,
    setValue,
    getValues,
    trigger,
    watch,
    ...props
}: FormItemProps) => {
    const {t} = useTranslation();
    const firstStep = useStore(firstStep$);
    const country = watch('country');

    const [options, setOptions] = useState<any[] | null>(null);

    const mapOptions = (options: any[], filter: string) =>
        options
            .filter(({countryCode}: {countryCode: string}) => countryCode === country)
            .map((value) => ({
                value: value.id,
                label: value.name,
                meta: {
                    name: value.name ?? '',
                },
            }))
            .filter(({label}: {label: string}) =>
                label.toLowerCase().includes(filter.toLowerCase()),
            );

    const loadOptions = async (filter: string) => {
        if (!options) {
            const data = await filterFactories();
            setOptions(data);
            return mapOptions(data, filter);
        }
        return mapOptions(options, filter);
    };

    return (
        <>
            <Controller
                control={control}
                {...props}
                render={({value, onChange}, {invalid}) => (
                    <AsyncSelect
                        key={country}
                        label={t('pages.orders.form.factoryName')}
                        isClearable
                        defaultOptions
                        cacheOptions
                        value={
                            options?.find((o) => o.countryCode === country)
                                ? {
                                      value,
                                      label: value
                                          ? firstStep?.factoryName || getValues(`factoryName`)
                                          : value,
                                  }
                                : null
                        }
                        onChange={(val: {value: string; meta: {name: string}}) => {
                            val ? onChange(val.value) : onChange(null);
                            setValue && setValue(`factoryName`, val?.meta?.name);
                            setValue && setValue(`productionLineId`, null);
                            setValue && setValue(`productionLineName`, null);
                            trigger && trigger('productionLineId');
                        }}
                        loadOptions={loadOptions}
                        hasError={invalid}
                    />
                )}
            />
        </>
    );
};

export const ProductionLineId = ({
    control,
    setValue,
    getValues,
    watch,
    ...props
}: FormItemProps) => {
    const {t} = useTranslation();
    const firstStep = useStore(firstStep$);
    const factoryId = watch('factoryId');

    const loadOptions = async (filter: string) => {
        let data = [];
        if (factoryId) {
            data = await filterFactoryLines(factoryId);
            data = data
                .map((value: any) => ({
                    value: value.id,
                    label: value.name,
                    meta: {
                        name: value.name ?? '',
                    },
                }))
                .filter(({label}: {label: string}) =>
                    label.toLowerCase().includes(filter.toLowerCase()),
                );
        }
        return data;
    };

    return (
        <>
            <Controller
                control={control}
                {...props}
                render={({value, onChange}) => (
                    <AsyncSelect
                        key={factoryId}
                        label={t('pages.orders.form.productionLineName')}
                        isClearable
                        cacheOptions
                        defaultOptions
                        value={
                            factoryId
                                ? {
                                      value,
                                      label: `${firstStep?.productionLineName ||
                                          getValues(`productionLineName`) ||
                                          value ||
                                          ''}`,
                                  }
                                : null
                        }
                        onChange={(val: {value: string; meta: {name: string}}) => {
                            val ? onChange(val.value) : onChange(null);
                            setValue && setValue(`productionLineName`, val?.meta?.name);
                        }}
                        loadOptions={loadOptions}
                    />
                )}
            />
        </>
    );
};

export const OrderForm = () => {
    const {t} = useTranslation();
    const firstStep = useStore(firstStep$);
    const {data: factoryCountries} = useStore(profileCountriesState$);
    const [availableFactories, setAvailableFactories] = useState<FactoriesListRawRow[]>([]);

    useEffect(() => {
        const getAvailableFactories = async () => filterFactories();
        getAvailableFactories()
            .then((response) => setAvailableFactories(response))
            .catch((e) => console.log(e));
    }, []);

    const countries = React.useMemo(
        () =>
            factoryCountries
                .filter((country) => {
                    const availableFactoryCountriesList = availableFactories.map(
                        ({countryCode}) => countryCode,
                    );
                    const uniqueFactoryCountriesList = Array.from(
                        new Set(availableFactoryCountriesList),
                    );
                    return uniqueFactoryCountriesList.includes(country.code);
                })
                .map(({code}: ProfileCountries) => ({
                    label: t(`dictionaries.countries.${code}`),
                    value: code,
                })) || [],
        [t, factoryCountries, availableFactories],
    );
    const {control, handleSubmit, formState, getValues, setValue, watch, trigger} = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(schema),
        defaultValues: {
            ...firstStep,
        },
    });
    const onHandleChanged = (data: OrderFormData) => {
        stepChanged(1);
        firstStepChanged(data);
    };
    return (
        <FormContainer>
            <form onSubmit={handleSubmit(onHandleChanged)}>
                <Grid container spacing={3}>
                    <Grid item md={4} xs={12}>
                        <Controller
                            control={control}
                            name="country"
                            render={({value, onChange}, {invalid}) => (
                                <Select
                                    onChange={(val: SelectOption<string>) => {
                                        onChange(val?.value || null);
                                        setValue && setValue(`productionLineId`, null);
                                        setValue && setValue(`productionLineName`, null);
                                        setValue && setValue(`factoryId`, null);
                                        setValue && setValue(`factoryName`, null);
                                        trigger && trigger('productionLineId');
                                    }}
                                    value={
                                        countries.filter(
                                            (el: SelectOption<string>) => el.value === value,
                                        )[0]
                                    }
                                    label={t('pages.orders.form.country')}
                                    hasError={invalid}
                                    options={countries}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item md={4} xs={12}>
                        <FactoryId
                            control={control}
                            name={`factoryId`}
                            setValue={setValue}
                            getValues={getValues}
                            trigger={trigger}
                            watch={watch}
                        />
                        <div style={{display: 'none'}}>
                            <Controller
                                control={control}
                                name="factoryName"
                                render={(props) => <input value={props.value} readOnly />}
                            />
                        </div>
                    </Grid>
                    <Grid item md={4} xs={12}>
                        <ProductionLineId
                            control={control}
                            name={`productionLineId`}
                            setValue={setValue}
                            getValues={getValues}
                            watch={watch}
                        />
                        <div style={{display: 'none'}}>
                            <Controller
                                control={control}
                                name="productionLineName"
                                render={(props) => <input value={props.value} readOnly />}
                            />
                        </div>
                    </Grid>
                </Grid>
                <NavigationButtonsContainer>
                    <span />
                    <Button
                        style={{width: 'auto'}}
                        variant={ButtonVariant.Normal}
                        type="submit"
                        disabled={formState.isValid === false}
                    >
                        {t('components.form.nextStep')}
                    </Button>
                </NavigationButtonsContainer>
            </form>
        </FormContainer>
    );
};
